import { inject, Injectable, InjectionToken } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { take, timeout } from 'rxjs/operators';
import { SESSION_HEARTBEAT_TIMEOUT_MS, SessionActivityService } from './session-activity.service';
import { classifyEnforcement, SESSION_POLICY_FIELDS, SessionPolicyService } from './session-policy.service';
import type { SessionHeartbeatResult } from './session-heartbeat.service';

/**
 * Makes ONE authenticated heartbeat call so the server records activity and returns its own idle
 * clock (see SessionHeartbeatService). The real provider is registered in `main.ts`. The default
 * is `null` (no call possible), which `staySignedIn` treats as a FAILED heartbeat, never as success.
 */
export const SESSION_HEARTBEAT = new InjectionToken<(() => Observable<SessionHeartbeatResult>) | null>('SESSION_HEARTBEAT', {
  providedIn: 'root',
  factory: () => null,
});

/**
 * State for the "you are about to be signed out" warning. The visual component
 * (SessionIdleWarningComponent) only renders `warning$`, `error$`, `pending$` and calls {@link staySignedIn}.
 *
 * "Stay signed in" extends the session ONLY after the server confirmed it: the client timer is
 * restarted from the server's own `idle_remaining_seconds`, never from a click alone. On failure (including a
 * {@link SESSION_HEARTBEAT_TIMEOUT_MS} timeout, D8) the warning stays visible, nothing is extended, `error$` becomes `true`,
 * the button is usable again (`pending$` false) for a MANUAL retry, and nothing is retried automatically.
 */
@Injectable({ providedIn: 'root' })
export class SessionIdleWarningService {
  private readonly heartbeat = inject(SESSION_HEARTBEAT);
  private readonly policy = inject(SessionPolicyService);
  private readonly activity = inject(SessionActivityService);
  private readonly warning = new BehaviorSubject<boolean>(false);
  private readonly failed = new BehaviorSubject<boolean>(false);
  private readonly inFlight = new BehaviorSubject<boolean>(false);
  private readonly stay = new Subject<void>();
  /** D8: identifies the current attempt; a timeout, a new attempt or a discard makes an older attempt's outcome stale. */
  private attempt = 0;

  /** `true` while the warning is showing. */
  readonly warning$: Observable<boolean> = this.warning.asObservable();
  /** `true` after a "Stay signed in" attempt failed ("Could not extend your session"); cleared on the next attempt or when the warning hides. */
  readonly error$: Observable<boolean> = this.failed.asObservable();
  /** `true` while a heartbeat is in flight. */
  readonly pending$: Observable<boolean> = this.inFlight.asObservable();
  /** Emits after a SUCCESSFUL heartbeat has been applied; the idle timer re-arms on it. */
  readonly staySignedIn$: Observable<void> = this.stay.asObservable();

  get isWarning(): boolean { return this.warning.value; }

  /** Shows or hides the warning. Hiding also clears a stale error. */
  setWarning(on: boolean): void {
    if (this.warning.value !== on) {
      this.warning.next(on);
    }
    if (!on && this.failed.value) {
      this.failed.next(false);
    }
  }

  /**
   * Asks the server to count activity and applies its answer. Ignored while a heartbeat is in flight.
   * Success: the policy and the server activity clock are updated from the response, then
   * {@link staySignedIn$} emits. Failure (no provider, error, 503, malformed/missing fields): no timer
   * is touched, the warning stays, and `error$` is `true`. No retry loop.
   */
  staySignedIn(): void {
    if (this.inFlight.value) {
      return;
    }
    this.failed.next(false);
    if (!this.heartbeat) {
      console.warn('[SessionIdleWarning] no heartbeat provider configured; the session was NOT extended.');
      this.failed.next(true);
      return;
    }
    this.inFlight.next(true);
    let settled = false;
    // Policy-state (late responses): the session generation at send time. A logout, 401 or brand-new session in the
    // meantime bumps it, and the result then belongs to a session that no longer exists: it is discarded.
    const generationAtSend = this.policy.generation;
    // D8: this attempt's id. The subscription below is torn down by take(1)/timeout/error, so a late emission of a
    // well-behaved provider cannot reach it; the id also covers a provider that ignores unsubscription.
    const attemptId = ++this.attempt;
    const stale = (): boolean => attemptId !== this.attempt || this.policy.generation !== generationAtSend || this.activity.isEnded;
    this.heartbeat().pipe(take(1), timeout({ first: SESSION_HEARTBEAT_TIMEOUT_MS })).subscribe({
      next: (result) => { settled = true; if (stale()) { this.discard(attemptId); } else { this.onResult(result); } },
      error: (err: unknown) => { settled = true; if (stale()) { this.discard(attemptId); } else { this.onFailure(err); } },
      complete: () => {
        // A provider that completes without emitting is a failure too.
        if (!settled) {
          if (stale()) { this.discard(attemptId); } else { this.onFailure(new Error('heartbeat completed without a result')); }
        }
      },
    });
  }

  /** Drops a heartbeat outcome that arrived for a session that has since ended or been replaced: no timer, policy or error state is touched. */
  private discard(attemptId: number): void {
    if (attemptId === this.attempt) {
      this.inFlight.next(false); // D8: only the newest attempt owns the in-flight flag
    }
  }

  /**
   * Applies a heartbeat result, or fails when it carries nothing usable.
   * @param result - Parsed heartbeat result.
   */
  private onResult(result: SessionHeartbeatResult): void {
    const receivedAtMs = result.receivedAtMs ?? Date.now();
    if (result.mode === 'off') {
      // NEW-1: an explicit Off is a trusted signal (after an enforced policy the client enters the server-off-suspended state per ENFORCE_TO_OFF_BEHAVIOUR; Phase 47B Q2).
      // The server acknowledged the touch but enforces no idle clock, so there is no server remaining time.
      // Fix3 item 3: Off is applied only for a bearer that names a tenant (rt.cid) consistent with the policy tenant. An omitted or null
      // tenant (`tenantId` missing from the result counts as null) is an incomplete answer, exactly like a live result: it applies nothing
      // and the manual retry stays available. A foreign tenant is discarded silently, as before.
      const offTenant = result.tenantId ?? null;
      if (offTenant === null) {
        this.onFailure(new Error('heartbeat Off result is incomplete: no usable rt.cid on the bearer'));
        return;
      }
      if (this.policy.tenantId !== null && offTenant !== this.policy.tenantId) {
        this.inFlight.next(false);
        return;
      }
      this.policy.applyFromResponse({ [SESSION_POLICY_FIELDS.enforcement]: 'off' }, offTenant);
      this.activity.recordAcknowledged(result.sentAtMs, receivedAtMs);
    } else {
      // D7: a result sent under another tenant's token (company switch while it was in flight) is discarded untouched.
      const tenant = result.tenantId ?? null;
      if (tenant !== null && this.policy.tenantId !== null && tenant !== this.policy.tenantId) {
        this.inFlight.next(false);
        return;
      }
      // C9: a live (enforce/shadow) heartbeat result names its tenant. `tenantId` is the rt.cid claim of the bearer it was sent
      // with (`null` = no usable claim). Fix3 item 3: an OMITTED `tenantId` is treated as `null`, so leaving it out can no longer
      // bypass the rule. A missing/unusable tenant or an omitted/foreign `customer_id` is an incomplete or foreign answer: never applied.
      if (this.isLiveBody(result.result)) {
        const bodyTenant = result.result[SESSION_POLICY_FIELDS.customerId];
        if (tenant === null || typeof bodyTenant !== 'number' || !Number.isInteger(bodyTenant)) {
          this.onFailure(new Error('heartbeat result is incomplete: no customer_id / unusable rt.cid'));
          return;
        }
        if (bodyTenant !== tenant) {
          this.inFlight.next(false); // names another tenant than the bearer it was sent with: never applied, never extends anything
          return;
        }
      }
      const outcome = this.policy.applyFromResponse(result.result, tenant, { sentAtMs: result.sentAtMs, receivedAtMs });
      if (outcome === 'tenant-mismatch') {
        this.inFlight.next(false); // the body names another tenant: never applied, never extends anything
        return;
      }
      // N2: a shadow/unknown policy is not applied, so there is nothing to extend.
      if (result.idleRemainingMs === null || !this.policy.enforced) {
        this.onFailure(new Error('heartbeat success carried no usable idle_remaining_seconds / idle policy'));
        return;
      }
      // C4: logout deadline = receipt time + remaining, warning = send time + remaining - warning.
      this.activity.syncFromRemaining(result.sentAtMs, this.policy.effectiveIdleMs, result.idleRemainingMs, receivedAtMs);
    }
    this.inFlight.next(false);
    this.failed.next(false);
    this.stay.next();
  }

  /** `true` for a heartbeat body that carries (or should carry) a policy: not Off / no_session / shadow_unavailable / untrusted, whose bodies name no tenant. */
  private isLiveBody(body: Record<string, unknown>): boolean {
    const kind = classifyEnforcement(body).kind;
    return kind === 'mode' || kind === 'absent';
  }

  /**
   * Records a failed attempt without touching any timer.
   * @param err - Failure cause (logged).
   */
  private onFailure(err: unknown): void {
    console.warn('[SessionIdleWarning] heartbeat failed; the session was NOT extended.', err);
    this.inFlight.next(false);
    this.failed.next(true);
  }
}
